/* eslint-disable prefer-rest-params */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ClassGroupInterface,
  PersonInterface,
  PurchaseInterface,
  SchoolBillingAddressInterface,
  SchoolInterface,
  SchoolProviderInterface,
  SchoolRoleMappingClassGroupInterface,
  SchoolRoleMappingInterface,
  StudentInterface,
} from '../../+models';
import { ModelApiBase, PolpoApi } from '../shared';

@Injectable({ providedIn: 'root' })
export class SchoolApi extends ModelApiBase {
  constructor(api: PolpoApi) {
    super(api, 'Schools');
  }

  findBySchoolCode(schoolCode: string | number) {
    const path = 'findBySchoolCode';

    return this.post(path, undefined, { schoolCode });
  }

  createClassGroup(schoolId: number, classGroup: Partial<ClassGroupInterface>) {
    const path = `${schoolId}/class-groups`;

    return this.post<ClassGroupInterface>(path, undefined, classGroup);
  }

  updateClassGroup(schoolId: number, classGroupId: number, classGroup: Partial<ClassGroupInterface>) {
    const path = `${schoolId}/class-groups/${classGroupId}`;

    return this.patch<ClassGroupInterface>(path, undefined, classGroup);
  }

  removeClassGroup(schoolId: number, classGroupId: number) {
    const path = `${schoolId}/class-groups/${classGroupId}`;

    return this.delete<{ count: number }>(path);
  }

  linkTeacherRolesClassGroup(schoolId: number, classGroupId: number, schoolRoleMappingIds: number[]) {
    const path = `${schoolId}/class-groups/${classGroupId}/link-teacher-roles`;

    return this.post(path, undefined, schoolRoleMappingIds);
  }

  linkStudentRolesClassGroup(
    schoolId: number,
    classGroupId: number,
    studentRoles: {
      schoolRoleMappingId: number;
      classNumber?: number;
    }[]
  ) {
    const path = `${schoolId}/class-groups/${classGroupId}/link-student-roles`;

    return this.post(path, undefined, studentRoles);
  }

  unlinkRolesClassGroup(schoolId: number, classGroupId: number, schoolRoleMappingIds: number[]) {
    const path = `${schoolId}/class-groups/${classGroupId}/unlink-roles`;

    return this.post(path, undefined, schoolRoleMappingIds);
  }

  getData(schoolId: number, fields?: string, since?: number) {
    const path = `${schoolId}/data`;

    return this.get(path);
  }

  createStudent(schoolId: number, student: Partial<StudentInterface>) {
    const path = `${schoolId}/students`;

    return this.post<{
      person: PersonInterface;
      schoolRoleMapping: SchoolRoleMappingInterface;
      schoolRoleMappingClassGroup: SchoolRoleMappingClassGroupInterface;
    }>(path, undefined, student);
  }

  createTeacher(schoolId: number, teacher: Partial<PersonInterface>) {
    const path = `${schoolId}/teachers`;

    return this.post<{
      person: PersonInterface;
      schoolRoleMapping: SchoolRoleMappingInterface;
    }>(path, undefined, teacher);
  }

  removeStudent(schoolId: number, studentId: number) {
    const path = `${schoolId}/students/${studentId}`;

    return this.delete<{ count: number }>(path);
  }

  removeStudents(schoolId: number, studentIds: number[]) {
    const path = `${schoolId}/students/remove-students`;

    return this.post(path, undefined, studentIds);
  }

  updateStudent(schoolId: number, studentId: number, student: Partial<StudentInterface>) {
    const path = `${schoolId}/students/${studentId}`;

    return this.patch<{
      person: PersonInterface;
      schoolRoleMapping: SchoolRoleMappingInterface;
      schoolRoleMappingClassGroup: SchoolRoleMappingClassGroupInterface;
    }>(path, undefined, student);
  }

  importStudents(schoolId: number, students: any[], deleteMissingRootNumbers: boolean = false) {
    const path = `${schoolId}/students/import`;

    const params = this._getHttpParams({ deleteMissingRootNumbers });

    return this.post(path, params, students);
  }

  updateTeacher(schoolId: number, teacherId: number, teacher: Partial<PersonInterface>) {
    const path = `${schoolId}/teachers/${teacherId}`;

    return this.patch<{
      person: PersonInterface;
      schoolRoleMapping: SchoolRoleMappingInterface[];
    }>(path, undefined, teacher);
  }

  removeTeacher(schoolId: number, teacherId: number) {
    const path = `${schoolId}/teachers/${teacherId}`;

    return this.delete<{ count: number }>(path);
  }

  createSchoolAdmin(schoolId: number, schoolAdmin: Partial<PersonInterface>) {
    const path = `${schoolId}/schoolAdmin`;

    return this.post<{
      person: PersonInterface;
      schoolRoleMapping: SchoolRoleMappingInterface;
    }>(path, undefined, schoolAdmin);
  }

  updateSchoolAdmin(schoolId: number, schoolAdminId: number, schoolAdmin: Partial<PersonInterface>) {
    const path = `${schoolId}/schoolAdmins/${schoolAdminId}`;

    return this.patch<{
      person: PersonInterface;
      schoolRoleMapping: SchoolRoleMappingInterface[];
    }>(path, undefined, schoolAdmin);
  }

  removeSchoolAdmin(schoolId: number, schoolAdminId: number) {
    const path = `${schoolId}/schoolAdmins/${schoolAdminId}`;

    return this.delete<{ count: number }>(path);
  }

  linkSchoolAdmin(schoolId: number, personId: number) {
    const path = `${schoolId}/link-school-admin/${personId}`;

    return this.post(path);
  }

  placeOrder(
    schoolId: number,
    productIds: number[],
    customerOrderReference = '',
    schoolBillingAddress?: SchoolBillingAddressInterface,
    fullDigital: number[] = []
  ) {
    const path = `${schoolId}/orders`;

    return this.post(path, undefined, { productIds, customerOrderReference, schoolBillingAddress, fullDigital });
  }

  updatePurchase(schoolId: number, purchaseId: number | string, purchase: Partial<PurchaseInterface>) {
    const path = `${schoolId}/update-purchase/${purchaseId}`;

    return this.patch(path, undefined, purchase);
  }

  deletePurchase(id: number) {
    const path = `purchase/${id}`;

    return this.delete<{ count: number }>(path);
  }

  createPurchase(schoolId: number, purchase: Partial<PurchaseInterface>) {
    const path = `${schoolId}/create-purchase`;

    return this.post<PurchaseInterface>(path, undefined, purchase);
  }

  public sendInviteMail(schoolId: number, teacherId: number): Observable<{ id: number; lastInvitation: Date }> {
    const path = `${schoolId}/people/${teacherId}/send-invite`;
    return this.post<{ id: number; lastInvitation: Date }>(path);
  }

  public createSchool(school: SchoolInterface): Observable<SchoolInterface> {
    const path = 'create';
    return this.post<SchoolInterface>(path, undefined, school);
  }

  public removeSchool(schoolId: number): Observable<SchoolInterface> {
    const path = `${schoolId}/remove`;
    return this.delete(path);
  }

  public updateSchool(schoolId: number, update: Partial<SchoolInterface>): Observable<SchoolInterface> {
    const path = `${schoolId}/update`;
    return this.patch<SchoolInterface>(path, undefined, update);
  }

  public updateStudentCode(
    schoolId: number,
    studentId: number,
    code: string
  ): Observable<{ schoolRoleMapping: SchoolRoleMappingInterface }> {
    const path = `${schoolId}/students/${studentId}/code`;
    const body = { personalCode: code };
    return this.patch<{ schoolRoleMapping: SchoolRoleMappingInterface }>(path, undefined, body);
  }

  public addStudentCoins(
    schoolId: number,
    studentId: number,
    coins: number
  ): Observable<{ schoolRoleMapping: SchoolRoleMappingInterface }> {
    const path = `${schoolId}/students/${studentId}/coins`;
    const body = { gameCoinsToAdd: coins };
    return this.patch<{ schoolRoleMapping: SchoolRoleMappingInterface }>(path, undefined, body);
  }

  public getForZip(zipCode: string): Observable<SchoolInterface[]> {
    const path = `get-for-zip/${zipCode}`;
    return this.get<SchoolInterface[]>(path);
  }

  public updateTransition(schoolId: number, type: string): Observable<SchoolInterface> {
    const path = `${schoolId}/update-year-transition/${type}`;
    return this.post<SchoolInterface>(path, undefined);
  }

  public unlinkStudentsInSchool(schoolId: number): Observable<SchoolRoleMappingInterface[]> {
    const path = `${schoolId}/unlink-students`;
    return this.post<SchoolRoleMappingInterface[]>(path, undefined);
  }

  public calculateSchoolBilling(schoolId: number) {
    const path = `${schoolId}/calculate-school-billing`;
    return this.get(path);
  }

  public createSchoolBillingAddress(schoolId: number, schoolBillingAddress): Observable<SchoolBillingAddressInterface> {
    const path = `${schoolId}/school-billing-address`;
    return this.post<SchoolBillingAddressInterface>(path, undefined, schoolBillingAddress);
  }

  public updateSchoolBillingAddress(
    schoolId: number,
    schoolBillingAddressId: number,
    update: Partial<SchoolBillingAddressInterface>
  ) {
    const path = `${schoolId}/school-billing-address/${schoolBillingAddressId}`;
    return this.patch<SchoolBillingAddressInterface>(path, undefined, update);
  }

  public deleteSchoolBillingAddress(schoolId: number, schoolBillingAddressId: number) {
    const path = `${schoolId}/school-billing-address/${schoolBillingAddressId}`;
    return this.delete<number>(path, undefined);
  }

  public unlinkProvider(
    schoolId: number,
    providerId: number
  ): Observable<{ schoolProvider: SchoolProviderInterface; schoolRoleMappingIds: number[] }> {
    const path = `${schoolId}/unlink-provider/${providerId}`;
    return this.delete<{ schoolProvider: SchoolProviderInterface; schoolRoleMappingIds: number[] }>(path);
  }

  public linkProvider(schoolId: number, provider: SchoolProviderInterface) {
    const path = `${schoolId}/link-provider`;
    return this.post<SchoolProviderInterface>(path, undefined, { schoolProvider: provider });
  }
}
